import { useQuery } from "@apollo/client";
import { ReactElement, useEffect, useRef, useState } from "react";
import { useSearchParams } from "solito/navigation";
import { amplitudeAddData, amplitudeClearKeys } from "src/api/amplitude/AmplitudeStore";
import GetGatheringAmplitudeInfoDocument from "src/api/graphql/__generated__/documents/GetGatheringAmplitudeInfoDocument";
import FaceFilterMode from "src/api/graphql/__generated__/enums/FaceFilterMode";
import GatheringAccessEnum from "src/api/graphql/__generated__/enums/GatheringAccessEnum";
import { GatheringHeaderInfoFragmentFragment } from "src/api/graphql/__generated__/graphql";
import { GatheringModalConfig } from "src/modals/configs/GatheringModal";
import RootModalContainer from "src/modals/containers/RootModalContainer";
import { StoreApi, useStore } from "zustand";
import { ReverseSortModeQueryParamLookup } from "../constants/SortModeQueryParamLookup";
import { GatheringContext } from "../GatheringContext";
import { GatheringStore, createGatheringStore } from "../gatheringStore";
import { GatheringEditMode, GatheringSortMode, GatheringViewMode } from "../types";
import GatheringDataSync from "./GatheringDataSync";
import GatheringPaletteWrapper from "./GatheringPaletteWrapper";

const AddAmplitudeMetadata = ({
	gatheringId,
	store,
}: {
	gatheringId: string;
	store: StoreApi<GatheringStore>;
}) => {
	const { data } = useQuery(GetGatheringAmplitudeInfoDocument, {
		variables: {
			input: {
				gatheringId,
			},
		},
	});

	const viewMode = useStore(store, (state) => state.viewMode);
	useEffect(() => {
		amplitudeAddData({
			gatheringViewMode: viewMode,
		});
		return () => {
			amplitudeClearKeys(["gatheringViewMode"]);
		};
	}, [viewMode]);

	useEffect(() => {
		const gatheringInfo = {
			...data?.getGathering,
		};
		delete gatheringInfo.__typename;

		amplitudeAddData({
			gathering: {
				...gatheringInfo,
				gatheringId: gatheringId,
			},
		});
		return () => {
			amplitudeClearKeys(["gathering"]);
		};
	}, [data?.getGathering, gatheringId]);

	return null;
};

const safeJson = (value: string | undefined | null, defaultValue: any) => {
	if (!value) return defaultValue;
	try {
		return JSON.parse(value);
	} catch {
		return defaultValue;
	}
};

const GatheringContextWrapper = ({
	children,
	gathering,
	editMode,
}: {
	children: ReactElement | ReactElement[];
	gathering: NonNullable<GatheringHeaderInfoFragmentFragment>;
	editMode?: GatheringEditMode;
}) => {
	const initialSearch = useRef(useSearchParams());
	const initialEditMode = useRef(editMode);
	const initialAccess = useRef(gathering.access);
	const [store] = useState(() => {
		const uIds = initialSearch.current?.get("uIds");
		const uploaders = initialSearch.current?.get("uploaders");
		const media = initialSearch.current?.get("media");
		const tags = initialSearch.current?.get("tags");
		const adminTags = initialSearch.current?.get("adminTags");
		const columns = initialSearch.current?.get("columns");
		const editMode = (() => {
			const edit = (initialEditMode.current ?? initialSearch.current?.get("edit")) as
				| GatheringEditMode
				| undefined;
			if (initialAccess.current && initialAccess.current !== GatheringAccessEnum.Admin) {
				return "None";
			}
			switch (edit) {
				case "Create":
				case "Edit":
				case "None":
					return edit;
				default:
					return "None";
			}
		})();
		const filterUserIds = safeJson(uIds, []);
		const filterUploaderIds = safeJson(uploaders, []);
		const filterPhotoVariant = safeJson(media, []);
		const filterLabels = safeJson(tags, []);
		const filterRestrictedLabels = safeJson(adminTags, []);
		const numColumnsTarget = columns ? parseInt(columns) : undefined;

		const viewMode: GatheringViewMode = (() => {
			if (
				filterUserIds.length > 0 ||
				filterUploaderIds.length > 0 ||
				filterPhotoVariant.length > 0 ||
				filterLabels.length > 0 ||
				filterRestrictedLabels.length > 0
			)
				return "Filter";
			const view = initialSearch.current?.get("view");
			switch (view) {
				case "All":
				case "Top":
				case "Hidden":
					return view;
				case "Uploads":
					return "AllCaller";
				default:
					return "All";
			}
		})();
		const sortMode: GatheringSortMode = (() => {
			const sort = initialSearch.current?.get("sort");
			return ReverseSortModeQueryParamLookup[sort ?? "upload"] ?? "UploadedTime";
		})();

		const filterMode = (() => {
			const mode = initialSearch.current?.get("filter") as FaceFilterMode | undefined;
			switch (mode) {
				case FaceFilterMode.Or:
				case FaceFilterMode.And:
					return mode;
				default:
					return FaceFilterMode.Or;
			}
		})();

		return createGatheringStore({
			...gathering,
			filterUserIds,
			filterUploaderIds,
			filterPhotoVariant,
			editMode,
			viewMode,
			filterMode,
			sortMode,
			filterLabels,
			filterRestrictedLabels,
			numColumnsTarget,
		});
	});

	const [contextValue] = useState(() => ({ store }));

	return (
		<>
			<GatheringDataSync gatheringId={gathering.gatheringId} store={store} />
			<AddAmplitudeMetadata gatheringId={gathering.gatheringId} store={store} />
			<GatheringContext.Provider value={contextValue}>
				<GatheringPaletteWrapper>{children}</GatheringPaletteWrapper>
			</GatheringContext.Provider>
			{/* Leaving this explicitly outside GatheringContext since on native it's not wrapped in GatheringContext */}
			<RootModalContainer config={GatheringModalConfig} />
		</>
	);
};

export default GatheringContextWrapper;
